/**
 * Stockage de la file du stand : UN ENREGISTREMENT PAR CLÉ (troisième
 * relecture du 23/09, constat N2).
 *
 * La file était un tableau relu puis réécrit en entier : deux onglets qui
 * ajoutaient en même temps s'écrasaient, et une action acceptée disparaissait.
 * Chaque action vit désormais sous sa propre clé `<préfixe><id>` ; l'index de
 * la file se DÉRIVE en parcourant les clés. Ajouter est un `setItem`, retirer
 * un `removeItem`, mettre à jour réécrit la seule action concernée : aucune
 * opération ne relit puis réécrit l'enregistrement d'un autre.
 *
 * Repli mémoire, enregistrement par enregistrement : une écriture refusée
 * (quota, navigation privée) garde l'enregistrement en mémoire, un retrait
 * impossible laisse une trace de retrait. À chaque opération, la mémoire est
 * recopiée vers le stockage dès qu'il répond de nouveau.
 */

/** Écritures en attente de stockage durable, par clé complète. */
const enAttente = new Map<string, unknown>();
/** Retraits en attente de stockage durable, par clé complète. */
const retraits = new Set<string>();

function stockage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Écrit puis RELIT : un enregistrement n'est durable que si le stockage le
 * rend tel quel. Un stockage qui accepte l'écriture mais refuse la lecture
 * (navigation privée, stockage bloqué) garde donc la copie en mémoire.
 */
function ecrireEtRelire(magasin: Storage, cle: string, valeur: unknown): boolean {
  const serialise = JSON.stringify(valeur);
  magasin.setItem(cle, serialise);
  try {
    return magasin.getItem(cle) === serialise;
  } catch {
    return false;
  }
}

/** Recopie vers le stockage ce que la mémoire garde pour `prefixe`. */
function synchroniser(prefixe: string): void {
  const magasin = stockage();
  if (!magasin) return;
  for (const [cle, valeur] of enAttente) {
    if (!cle.startsWith(prefixe)) continue;
    try {
      if (ecrireEtRelire(magasin, cle, valeur)) enAttente.delete(cle);
    } catch {
      /* toujours refusé : l'enregistrement reste en mémoire */
    }
  }
  for (const cle of retraits) {
    if (!cle.startsWith(prefixe)) continue;
    try {
      magasin.removeItem(cle);
      retraits.delete(cle);
    } catch {
      /* toujours refusé : la trace de retrait reste */
    }
  }
}

export interface LectureEnregistrements<T> {
  /** Par identifiant : le stockage, recouvert par la mémoire, moins les retraits. */
  readonly enregistrements: Map<string, T>;
  /** Le stockage a-t-il pu être PARCOURU ? « Illisible » n'est pas « vide ». */
  readonly lisible: boolean;
}

/** Lit tous les enregistrements dont la clé commence par `prefixe`. */
export function lireEnregistrements<T>(prefixe: string): LectureEnregistrements<T> {
  synchroniser(prefixe);
  const enregistrements = new Map<string, T>();
  let lisible = true;
  const magasin = stockage();
  if (!magasin) {
    lisible = false;
  } else {
    try {
      const cles: string[] = [];
      for (let rang = 0; rang < magasin.length; rang += 1) {
        const cle = magasin.key(rang);
        if (cle !== null && cle.startsWith(prefixe)) cles.push(cle);
      }
      for (const cle of cles) {
        const brut = magasin.getItem(cle);
        if (brut === null) continue;
        try {
          enregistrements.set(cle.slice(prefixe.length), JSON.parse(brut) as T);
        } catch {
          // Un enregistrement illisible n'est ni effacé ni écrasé : il reste là,
          // et la lecture le dit.
          lisible = false;
        }
      }
    } catch {
      lisible = false;
    }
  }
  for (const [cle, valeur] of enAttente) {
    if (cle.startsWith(prefixe)) enregistrements.set(cle.slice(prefixe.length), valeur as T);
  }
  for (const cle of retraits) {
    if (cle.startsWith(prefixe)) enregistrements.delete(cle.slice(prefixe.length));
  }
  return { enregistrements, lisible };
}

/** Écrit UN enregistrement. `true` s'il est durable, `false` s'il reste en mémoire. */
export function ecrireEnregistrement(prefixe: string, id: string, valeur: unknown): boolean {
  synchroniser(prefixe);
  const cle = `${prefixe}${id}`;
  retraits.delete(cle);
  const magasin = stockage();
  try {
    if (!magasin) throw new Error('stockage indisponible');
    if (!ecrireEtRelire(magasin, cle, valeur)) throw new Error('relecture impossible');
    enAttente.delete(cle);
    return true;
  } catch {
    enAttente.set(cle, valeur);
    return false;
  }
}

/** Retire UN enregistrement. `true` si le retrait est durable. */
export function retirerEnregistrement(prefixe: string, id: string): boolean {
  synchroniser(prefixe);
  const cle = `${prefixe}${id}`;
  enAttente.delete(cle);
  const magasin = stockage();
  try {
    if (!magasin) throw new Error('stockage indisponible');
    magasin.removeItem(cle);
    retraits.delete(cle);
    return true;
  } catch {
    retraits.add(cle);
    return false;
  }
}

/** Un retrait de cet enregistrement attend-il encore le stockage ? */
export function retraitEnAttente(prefixe: string, id: string): boolean {
  return retraits.has(`${prefixe}${id}`);
}

/**
 * Met à jour UN enregistrement existant — et lui seul. Absent (retiré entre
 * temps, y compris par un autre onglet), il n'est pas recréé.
 */
export function mettreAJourEnregistrement<T>(
  prefixe: string,
  id: string,
  maj: (valeur: T) => T,
): boolean {
  const actuel = lireEnregistrements<T>(prefixe).enregistrements.get(id);
  if (actuel === undefined) return false;
  return ecrireEnregistrement(prefixe, id, maj(actuel));
}

/** Quelque chose de `prefixe` n'est-il PAS durable (mémoire, retrait, stockage illisible) ? */
export function nonDurable(prefixe: string): boolean {
  synchroniser(prefixe);
  for (const cle of enAttente.keys()) if (cle.startsWith(prefixe)) return true;
  for (const cle of retraits) if (cle.startsWith(prefixe)) return true;
  return !lireEnregistrements(prefixe).lisible;
}

/** Les clés (complètes) du stockage qui satisfont `motif` — pour les migrations. */
export function clesDuStockage(motif: RegExp): string[] {
  const magasin = stockage();
  if (!magasin) return [];
  const cles: string[] = [];
  try {
    for (let rang = 0; rang < magasin.length; rang += 1) {
      const cle = magasin.key(rang);
      if (cle !== null && motif.test(cle)) cles.push(cle);
    }
  } catch {
    return [];
  }
  return cles;
}

/** Lit une clé ENTIÈRE (ancien format tableau). `null` : absente ; `undefined` : illisible. */
export function lireCleBrute(cle: string): unknown[] | null | undefined {
  const magasin = stockage();
  if (!magasin) return undefined;
  try {
    const brut = magasin.getItem(cle);
    if (brut === null) return null;
    const valeur = JSON.parse(brut) as unknown;
    return Array.isArray(valeur) ? valeur : undefined;
  } catch {
    return undefined;
  }
}

/** Retire une clé entière (ancien format), une fois tout recopié durablement. */
export function retirerCleBrute(cle: string): void {
  try {
    stockage()?.removeItem(cle);
  } catch {
    /* rien de plus à faire : la source reste, une migration suivante reprendra */
  }
}

/** Oublie la mémoire de repli (tests). */
export function oublierMemoireDeRepli(): void {
  enAttente.clear();
  retraits.clear();
}
